import { computed, ref, watch } from "vue";
import { useCookieStorage } from "@/shared/composables/use-storage";
import useWecomaDatalayer from "@/shared/composables/wecoma/use-wecoma-datalayer";
import eventBus from "@/shared/utils/event-bus";
import { GlobalEvents } from "@/shared/model/enums/global-events";

const COOKIE_EXPIRATION = 365;
const ALL_RECHECK_EXPIRATION = 30;
const SOME_RECHECK_EXPIRATION = 60;
const COOKIE_NAME = "cbada";
const CONSENT_CACHE_COOKIE_NAME = "consents";

type WecomaCookie = {
    createdAt: number; // seconds since start of epoch
};

type CookieBarMode = "initial" | "someRecheckExpired" | "allRecheckExpired";

export type CookieType = "essential" | "analytics" | "marketing" | "thirdpartycontent";

const cookieBarMode = ref<CookieBarMode>("initial");
const cookieBarModeExpiration = computed(() => {
    switch (cookieBarMode.value) {
        case "allRecheckExpired":
            return ALL_RECHECK_EXPIRATION;
        case "someRecheckExpired":
            return SOME_RECHECK_EXPIRATION;
        case "initial":
        default:
            return null;
    }
});

const consentsCached = ref<Record<CookieType, boolean> | null>(null);

function initializeConsentsCached() {
    if (consentsCached.value === null) {
        const data = useCookieStorage().getItem(CONSENT_CACHE_COOKIE_NAME);

        consentsCached.value = {
            essential: true,
            analytics: data?.[0] === "1",
            marketing: data?.[1] === "1",
            thirdpartycontent: data?.[2] === "1",
        };
    }
}
watch(
    () => consentsCached.value,
    (newVal) => {
        if (newVal) {
            const getCookieValue = (val: boolean) => (val ? "1" : "0");
            useCookieStorage().setItem(
                CONSENT_CACHE_COOKIE_NAME,
                getCookieValue(newVal.analytics) +
                    getCookieValue(newVal.marketing) +
                    getCookieValue(newVal.thirdpartycontent),
                "essential",
                {
                    expires: 365,
                    sameSite: "strict",
                },
            );
        }
    },
    { deep: true },
);

const wecomaLoaded = new Promise(async (resolve) => {
    Wecoma.onready.push(resolve);
}).then(async () => {
    const [analyticsConsented, marketingConsented, thirdPartyConsented] = await Promise.all([
        _gdpr.wecoma.isConsented("analytics"),
        _gdpr.wecoma.isConsented("marketing"),
        _gdpr.wecoma.isConsented("thirdpartycontent"),
    ]);
    await initializeConsentsCached();

    consentsCached.value.analytics = analyticsConsented;
    consentsCached.value.marketing = marketingConsented;
    consentsCached.value.thirdpartycontent = thirdPartyConsented;
});

export default function useWecomaIntegration() {
    let currentCookieValue: WecomaCookie | null = null;

    const {
        pushViewCookieBarInitial,
        pushViewCookieBarRecheckAll,
        pushViewCookieBarRecheckSome,
        pushAllPurposesConsentClick,
        pushOnlyNecessaryPurposesConsent,
        pushRedirectToPreferenceCenter,
        pushReditectToPrivacyPolicy,
        pushThirdPartyConsent,
        pushLinksClickConsentBar,
    } = useWecomaDatalayer(cookieBarMode, cookieBarModeExpiration);

    const getNowTimestampInSeconds = () => Math.floor(Date.now() / 1000);

    const createWecomaCookieObject: () => WecomaCookie = () => {
        return {
            createdAt: getNowTimestampInSeconds(),
        };
    };

    const setWecomaCookie = () => {
        useCookieStorage().setItem(COOKIE_NAME, window.btoa(JSON.stringify(createWecomaCookieObject())), "essential", {
            //domain: ".sazka.cz", - TODO: Remove comment on prod?
            expires: COOKIE_EXPIRATION,
            sameSite: "strict",
        });
    };

    const getWecomaCookieCreateTimestamp = () => {
        if (!currentCookieValue) {
            const val = useCookieStorage().getItem(COOKIE_NAME);
            if (val) {
                currentCookieValue = JSON.parse(window.atob(val));
            }
        }

        return currentCookieValue?.createdAt;
    };

    const wecomaCookieExists = () => {
        return !!getWecomaCookieCreateTimestamp();
    };

    const allPurposesConsented = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesConsented(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const allPurposesRevoked = async () => {
        await wecomaLoaded;
        return await _gdpr.wecoma.allPurposesRevoked(_gdpr.wecoma.clientAuthKey, ["consentbar"]);
    };

    const daysToSeconds = (d: number) => d * 24 * 60 * 60;

    const recheckAllExpired = () => {
        return getWecomaCookieCreateTimestamp() < getNowTimestampInSeconds() - daysToSeconds(ALL_RECHECK_EXPIRATION);
    };

    const recheckSomeExpired = () => {
        return getWecomaCookieCreateTimestamp() < getNowTimestampInSeconds() - daysToSeconds(SOME_RECHECK_EXPIRATION);
    };

    const shouldCookieBarOpen = async () => {
        if (!wecomaCookieExists()) {
            pushViewCookieBarInitial();
            return true;
        }

        if (await allPurposesConsented()) {
            return false;
        }

        if (await allPurposesRevoked()) {
            if (recheckAllExpired()) {
                cookieBarMode.value = "allRecheckExpired";
                pushViewCookieBarRecheckAll();
                return true;
            }
        } else {
            if (recheckSomeExpired()) {
                cookieBarMode.value = "someRecheckExpired";
                pushViewCookieBarRecheckSome();
                return true;
            }
        }

        return false;
    };

    const consentToAllPurposes = async () => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: ["browser"],
        });

        for (const purpose of purposes) {
            purpose.consent();
        }

        pushAllPurposesConsentClick(purposes?.map((p) => p.code)?.join(","));

        setWecomaCookie();

        consentsCached.value.analytics = true;
        consentsCached.value.marketing = true;
        consentsCached.value.thirdpartycontent = true;

        eventBus.emit(GlobalEvents.ConsentsChanged);
    };

    const redirectToPreferenceCenter = (variant: "text" | "button") => {
        pushLinksClickConsentBar();
        pushRedirectToPreferenceCenter(variant);
    };

    const redirectToPrivacyPolicy = () => {
        pushLinksClickConsentBar();
        pushReditectToPrivacyPolicy();
    };

    const cookieTypeConsentedCached = (cookieType: CookieType): boolean => {
        if (consentsCached.value === null) {
            initializeConsentsCached(); // make sure that the consents are loaded before the first call to this function
        }
        return consentsCached.value[cookieType];
    };

    const analyticsConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("analytics");
    };

    const marketingConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("marketing");
    };

    const thirdPartyConsented = async (): Promise<boolean> => {
        await wecomaLoaded;
        return await _gdpr.wecoma.isConsented("thirdpartycontent");
    };

    const consentToThirdParty = async () => {
        const purpose = await _gdpr.wecoma.retrievePurpose("thirdpartycontent");
        await purpose.consent();
        pushThirdPartyConsent();
        consentsCached.value.thirdpartycontent = true;
    };

    const consentToOnlyNecessaryPurposes = async () => {
        await wecomaLoaded;
        const purposes = await _gdpr.wecoma.retrievePurposes({
            contexts: ["consentbar"],
            authKeyTypes: ["browser"],
        });

        for (const purpose of purposes) {
            purpose.revoke();
        }

        pushOnlyNecessaryPurposesConsent(purposes?.map((p) => p.code)?.join(","));

        setWecomaCookie();
        consentsCached.value.analytics = false;
        consentsCached.value.marketing = false;
        consentsCached.value.thirdpartycontent = false;
    };

    return {
        shouldCookieBarOpen,
        consentToAllPurposes,
        redirectToPreferenceCenter,
        redirectToPrivacyPolicy,
        cookieTypeConsentedCached,
        analyticsConsented,
        marketingConsented,
        thirdPartyConsented,
        consentToThirdParty,
        consentToOnlyNecessaryPurposes,
    };
}
